/** Going live (plan §10.4): "Request go-live" (optional note) → "Requested on … — we'll let you know"
 *  (withdraw while our team has not answered) → "Going live…" → "You're live". A rejection shows our
 *  team's note with "Request again".
 *
 *  The status comes from `state.go_live` (contract `OnboardingGoLive`); the page passes the request
 *  and withdraw calls (`POST go_live_request/`, `POST go_live_request/withdraw/`, WP B5) and re-reads
 *  the state after each. A user without `tour_operator.write` (or a page without the calls) sees the
 *  status only. */
import CelebrationIcon from '@mui/icons-material/Celebration';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import moment from 'moment';
import * as React from 'react';

import { errorCode } from './api';
import { ErrorLine, Section } from './components';
import { ERROR_CODES, GO_LIVE_STATUSES } from './labels';
import type { OnboardingState } from './types';

/** The note's limit (the contract's `maxLength`). */
export const NOTE_MAX = 2000;

/** "17 Oct 2026, 14:00" in the browser's zone, or null. */
export const requestedLabel = (ts: string | null | undefined) => (ts && moment(ts).isValid() ? moment(ts).format('D MMM YYYY, HH:mm') : null);

interface Props {
    state: OnboardingState;
    canWrite: boolean;
    /** Request go-live with an optional note (WP B5). */
    onRequest?: (note: string) => Promise<unknown>;
    /** Withdraw the open request (WP B5). */
    onWithdraw?: () => Promise<unknown>;
}

export default function GoLivePanel({ state, canWrite, onRequest, onWithdraw }: Props) {
    const { status, note } = state.go_live;
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    const [draft, setDraft] = React.useState('');
    // `request_conflict`: requests made at the same moment (another tab, a double click) — one of them
    // is open; the page re-reads the state, and a short note says so instead of an error.
    const [conflict, setConflict] = React.useState(false);

    const act = async (action: () => Promise<unknown>, after?: () => void) => {
        setBusy(true);
        setError(null);
        setConflict(false);
        try {
            await action();
            after?.();
        } catch (e) {
            if (errorCode(e) === 'request_conflict') {
                setConflict(true);
                after?.();
            } else {
                setError(e);
            }
        } finally {
            setBusy(false);
        }
    };

    const canRequest = canWrite && !!onRequest && (status === 'none' || status === 'rejected');
    const requestForm = canRequest ? (
        <Stack spacing={1} data-testid="go-live-form">
            <TextField
                label="Note for our team (optional)"
                multiline
                minRows={2}
                value={draft}
                onChange={(ev) => setDraft(ev.target.value.slice(0, NOTE_MAX))}
                disabled={busy}
                fullWidth
                size="small"
                inputProps={{ maxLength: NOTE_MAX }}
                helperText={draft.length > NOTE_MAX - 200 ? `${draft.length} / ${NOTE_MAX}` : 'Anything our team should know before switching your account on.'}
            />
            <Box>
                <Button
                    variant="contained"
                    disabled={busy}
                    onClick={() => act(() => onRequest!(draft), () => setDraft(''))}
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {status === 'rejected' ? 'Request again' : 'Request go-live'}
                </Button>
            </Box>
        </Stack>
    ) : null;

    let body: React.ReactNode;
    switch (status) {
        case 'none':
            body = (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
                        When you&apos;ve checked your tours and bookings, ask us to go live. Our team reviews your request and then
                        switches on the messages to your guests. Until then, no guest hears from us.
                    </Typography>
                    {requestForm}
                </>
            );
            break;
        case 'requested':
            body = (
                <>
                    <Typography variant="body2" data-testid="go-live-requested">
                        {requestedLabel(state.go_live.requested_ts)
                            ? `Requested on ${requestedLabel(state.go_live.requested_ts)} — we'll let you know.`
                            : "You've asked to go live — we'll let you know."}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
                        Our team checks your setup before switching on the messages to your guests.
                    </Typography>
                    {canWrite && onWithdraw && (
                        <Box>
                            <Button size="small" disabled={busy} onClick={() => act(onWithdraw)}>Withdraw request</Button>
                        </Box>
                    )}
                </>
            );
            break;
        case 'approved':
        case 'running':
            body = (
                <Stack direction="row" spacing={1} alignItems="center" data-testid="go-live-running">
                    <CircularProgress size={16} />
                    <Typography variant="body2">
                        {status === 'approved' ? 'Your request was approved — going live shortly.' : 'Going live…'}
                    </Typography>
                </Stack>
            );
            break;
        case 'live':
            body = (
                <Stack direction="row" spacing={1} alignItems="center" data-testid="go-live-live">
                    <CelebrationIcon color="success" />
                    <Typography variant="body2">You&apos;re live. Your guests now receive your messages.</Typography>
                </Stack>
            );
            break;
        case 'rejected':
            body = (
                <>
                    <Alert severity="warning" data-testid="go-live-rejected">
                        {note ? (
                            <>
                                <strong>Our team&apos;s note:</strong> {note}
                            </>
                        ) : (
                            'Your request was not approved yet.'
                        )}
                    </Alert>
                    {requestForm}
                </>
            );
            break;
        default:
            body = <Typography variant="body2">{GO_LIVE_STATUSES[status] || String(status)}</Typography>;
    }

    return (
        <Section title="Go live" testId="go-live">
            <Stack spacing={1.5}>
                {body}
                {conflict && (
                    <Typography variant="body2" color="text.secondary" data-testid="go-live-conflict">{ERROR_CODES.request_conflict}</Typography>
                )}
                <ErrorLine error={error} testId="go-live-error" />
            </Stack>
        </Section>
    );
}
